import type { EventType, EventTypeTaskTemplate } from "@kitchen/schemas";
import { Button, Chip, faDigits, Field, Panel, Switch } from "@kitchen/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { eventsApi } from "../../api.js";
import { askReason, ErrorLine, useAction } from "../common.js";

export function EventTypesTab() {
  const {
    data: types,
    isLoading,
    error,
  } = useQuery<EventType[]>({
    queryKey: ["events", "types"],
    queryFn: () => eventsApi.listEventTypes(),
  });

  const [showCreate, setShowCreate] = useState(false);

  return (
    <div className="stack" style={{ gap: "var(--s4)" }}>
      <div className="row wrap" style={{ justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <h2 className="t-title" style={{ margin: 0 }}>
            انواع مراسم و قالب‌های چک‌لیست کارها
          </h2>
          <p className="muted" style={{ margin: "4px 0 0 0", fontSize: "13px" }}>
            با تعریف قالب برای هر نوع مراسم، هنگام ثبت مراسم جدید کارهای استاندارد به‌طور خودکار بر حسب زمان
            موعد تولید می‌شوند.
          </p>
        </div>
        <Button
          variant="primary"
          icon="plus"
          onClick={() => {
            setShowCreate(!showCreate);
          }}
        >
          {showCreate ? "انصراف" : "تعریف نوع مراسم جدید"}
        </Button>
      </div>

      <ErrorLine error={error instanceof Error ? error.message : null} />

      {showCreate && (
        <CreateEventTypeForm
          onCreated={() => {
            setShowCreate(false);
          }}
        />
      )}

      {isLoading ? (
        <p className="muted">در حال بارگذاری انواع مراسم...</p>
      ) : (types ?? []).length === 0 ? (
        <Panel>
          <p className="muted">هیچ نوع مراسمی تعریف نشده است. یک نوع مراسم با قالب‌های کاری تعریف کنید.</p>
        </Panel>
      ) : (
        <div
          className="grid"
          style={{ gridTemplateColumns: "repeat(auto-fill, minmax(360px, 1fr))", gap: "var(--s4)" }}
        >
          {(types ?? []).map((t) => (
            <EventTypeCard key={t.id} type={t} />
          ))}
        </div>
      )}
    </div>
  );
}

function EventTypeCard({ type }: { type: EventType }) {
  const refresh = [["events", "types"]];
  const archiveAction = useAction(async () => {
    const reason = askReason(type.name);
    if (!reason) return;
    await eventsApi.archiveEventType(type.id, type.version);
  }, refresh);

  return (
    <div className="card stack" style={{ gap: "var(--s3)", padding: "var(--s4)" }}>
      <div className="row wrap" style={{ justifyContent: "space-between", alignItems: "center" }}>
        <h3 style={{ margin: 0, fontWeight: 700, fontSize: "16px" }}>{type.name}</h3>
        <Chip tone={type.active ? "ok" : "dark"}>{type.active ? "فعال" : "غیرفعال"}</Chip>
      </div>

      {type.description && (
        <p className="muted" style={{ margin: 0, fontSize: "13px" }}>
          {type.description}
        </p>
      )}

      <div style={{ borderTop: "1px solid var(--card-line)", paddingTop: "var(--s2)" }}>
        <div style={{ fontSize: "12px", fontWeight: 600, color: "var(--muted)", marginBottom: "var(--s2)" }}>
          قالب کارهای خودکار ({faDigits(type.taskTemplates.length)} کار):
        </div>
        {type.taskTemplates.length === 0 ? (
          <span className="muted" style={{ fontSize: "12px" }}>
            بدون کار پیش‌فرض
          </span>
        ) : (
          <ul style={{ margin: 0, paddingRight: "18px", fontSize: "13px", lineHeight: "1.6" }}>
            {type.taskTemplates.map((task, idx) => {
              const hours = Math.round(task.relativeOffsetMinutes / 60);
              return (
                <li key={idx}>
                  <strong>{task.title}</strong>
                  <span className="muted" style={{ fontSize: "11px", marginRight: "6px" }}>
                    ({hours <= 0 ? `${faDigits(Math.abs(hours))} ساعت قبل` : `${faDigits(hours)} ساعت بعد`}
                    {task.defaultRole ? ` | ${task.defaultRole}` : ""})
                  </span>
                  {task.checklist.length > 0 && (
                    <span className="chip plain" style={{ fontSize: "10px", marginRight: "4px" }}>
                      {faDigits(task.checklist.length)} مورد
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="row" style={{ justifyContent: "flex-end", marginTop: "auto", paddingTop: "var(--s2)" }}>
        <Button
          variant="ghost"
          size="md"
          icon="x"
          disabled={archiveAction.busy}
          onClick={() => {
            archiveAction.run({});
          }}
        >
          بایگانی
        </Button>
      </div>
    </div>
  );
}

function CreateEventTypeForm({ onCreated }: { onCreated: () => void }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [active, setActive] = useState(true);

  // Template tasks list
  const [templates, setTemplates] = useState<EventTypeTaskTemplate[]>([
    {
      title: "خرید مواد اولیه و پروتئین",
      relativeOffsetMinutes: -2880, // -48 hours
      defaultRole: "مسئول تدارکات",
      checklist: ["گوشت و مرغ تازه", "برنج و روغن", "ادویه و سبزیجات"],
      note: "",
    },
    {
      title: "آماده‌سازی و مرینیت",
      relativeOffsetMinutes: -1440, // -24 hours
      defaultRole: "سرآشپز",
      checklist: ["سیخ‌گیری کباب", "مرینیت جوجه", "تهیه سالاد"],
      note: "",
    },
    {
      title: "طبخ و بارگیری غذا",
      relativeOffsetMinutes: -240, // -4 hours
      defaultRole: "سرآشپز",
      checklist: ["دم‌کشی برنج", "تست طعم و حرارت", "بسته‌بندی هات‌باکس"],
      note: "",
    },
    {
      title: "پذیرایی و نظافت سالن",
      relativeOffsetMinutes: 120, // +2 hours
      defaultRole: "میزبان ارشد",
      checklist: ["سرو نوشیدنی", "شارژ ظروف", "جمع‌آوری و شستشو"],
      note: "",
    },
  ]);

  const [newTaskTitle, setNewTaskTitle] = useState("");
  const [newTaskHours, setNewTaskHours] = useState("-2");
  const [newTaskRole, setNewTaskRole] = useState("");
  const [newTaskChecklist, setNewTaskChecklist] = useState("");

  const refresh = [["events", "types"]];
  const createAction = useAction(async () => {
    if (!name.trim()) return;
    await eventsApi.createEventType({
      name: name.trim(),
      description: description.trim(),
      active,
      taskTemplates: templates,
    });
    onCreated();
  }, refresh);

  const addTemplate = () => {
    if (!newTaskTitle.trim()) return;
    const items = newTaskChecklist
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean);

    setTemplates([
      ...templates,
      {
        title: newTaskTitle.trim(),
        relativeOffsetMinutes: (Number(newTaskHours) || 0) * 60,
        defaultRole: newTaskRole.trim(),
        checklist: items,
        note: "",
      },
    ]);
    setNewTaskTitle("");
    setNewTaskHours("-2");
    setNewTaskRole("");
    setNewTaskChecklist("");
  };

  const removeTemplate = (idx: number) => {
    setTemplates(templates.filter((_, i) => i !== idx));
  };

  return (
    <Panel title="تعریف نوع مراسم و قالب کارها">
      <div className="stack" style={{ gap: "var(--s4)" }}>
        <div className="row wrap" style={{ gap: "var(--s3)" }}>
          <div style={{ flex: 1, minWidth: "220px" }}>
            <Field
              id="event-type-name"
              label="نام نوع مراسم *"
              placeholder="مثلاً: جشن عقد و عروسی، سمینار شرکتی"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
              }}
            />
          </div>
          <div style={{ flex: 2, minWidth: "280px" }}>
            <Field
              id="event-type-desc"
              label="توضیحات"
              placeholder="شرح کوتاه درباره ویژگی‌ها و تشریفات این نوع مراسم"
              value={description}
              onChange={(e) => {
                setDescription(e.target.value);
              }}
            />
          </div>
          <div style={{ display: "flex", alignItems: "flex-end", paddingBottom: "8px" }}>
            <Switch label="فعال" checked={active} onChange={setActive} />
          </div>
        </div>

        {/* Templates editor */}
        <div
          className="stack"
          style={{ gap: "var(--s2)", borderTop: "1px solid var(--card-line)", paddingTop: "var(--s3)" }}
        >
          <h4 style={{ margin: 0, fontWeight: 600 }}>
            کارهای قالب چک‌لیست ({faDigits(templates.length)} کار)
          </h4>

          {templates.map((tpl, i) => {
            const hours = Math.round(tpl.relativeOffsetMinutes / 60);
            return (
              <div
                key={i}
                className="row wrap"
                style={{
                  justifyContent: "space-between",
                  alignItems: "center",
                  background: "var(--card)",
                  padding: "var(--s2) var(--s3)",
                  borderRadius: "var(--r-card)",
                  border: "1px solid var(--card-line)",
                }}
              >
                <div>
                  <strong>{tpl.title}</strong>
                  <span className="muted" style={{ marginRight: "8px", fontSize: "12px" }}>
                    {hours <= 0
                      ? `${faDigits(Math.abs(hours))} ساعت قبل از شروع`
                      : `${faDigits(hours)} ساعت بعد از شروع`}
                    {tpl.defaultRole ? ` | مسئول: ${tpl.defaultRole}` : ""}
                  </span>
                  {tpl.checklist.length > 0 && (
                    <div style={{ fontSize: "11px", color: "var(--muted)", marginTop: "2px" }}>
                      چک‌لیست: {tpl.checklist.join("، ")}
                    </div>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="md"
                  icon="x"
                  onClick={() => {
                    removeTemplate(i);
                  }}
                >
                  حذف
                </Button>
              </div>
            );
          })}

          {/* Add template line */}
          <div
            className="stack"
            style={{
              background: "var(--panel)",
              padding: "var(--s3)",
              borderRadius: "var(--r-card)",
              border: "1px dashed var(--line)",
              gap: "var(--s2)",
            }}
          >
            <div style={{ fontSize: "13px", fontWeight: 600 }}>افزودن کار به قالب:</div>
            <div className="row wrap" style={{ gap: "var(--s2)" }}>
              <div style={{ flex: 2, minWidth: "180px" }}>
                <Field
                  id="tpl-title"
                  label="عنوان کار"
                  placeholder="مثلاً: هماهنگی گل‌آرایی"
                  value={newTaskTitle}
                  onChange={(e) => {
                    setNewTaskTitle(e.target.value);
                  }}
                />
              </div>
              <div style={{ flex: 1, minWidth: "130px" }}>
                <Field
                  id="tpl-hours"
                  label="ساعت نسبت به شروع"
                  type="number"
                  placeholder="مثلاً -4 یا 2"
                  value={newTaskHours}
                  onChange={(e) => {
                    setNewTaskHours(e.target.value);
                  }}
                />
              </div>
              <div style={{ flex: 1, minWidth: "140px" }}>
                <Field
                  id="tpl-role"
                  label="نقش پیشنهادی"
                  placeholder="مثلاً: مسئول تشریفات"
                  value={newTaskRole}
                  onChange={(e) => {
                    setNewTaskRole(e.target.value);
                  }}
                />
              </div>
            </div>
            <Field
              id="tpl-checklist"
              label="موارد چک‌لیست (هر مورد در یک خط)"
              placeholder="بررسی ورودی سالن&#10;تست چراغ‌ها"
              value={newTaskChecklist}
              onChange={(e) => {
                setNewTaskChecklist(e.target.value);
              }}
            />
            <div className="row" style={{ justifyContent: "flex-end" }}>
              <Button variant="default" icon="plus" onClick={addTemplate}>
                افزودن این کار
              </Button>
            </div>
          </div>
        </div>

        <ErrorLine error={createAction.error} />

        <div className="row" style={{ justifyContent: "flex-end", gap: "var(--s2)" }}>
          <Button variant="ghost" onClick={onCreated}>
            انصراف
          </Button>
          <Button
            variant="primary"
            icon="check"
            disabled={!name.trim() || createAction.busy}
            onClick={() => {
              createAction.run({});
            }}
          >
            ذخیره نوع مراسم
          </Button>
        </div>
      </div>
    </Panel>
  );
}
